"use client";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Input, Typography } from "@/components/ui";
import { NewButton } from "@/components/ui/NewButton";
import { env } from "@/lib/environment";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useUTMContext } from "@/lib/context/UTMProvider";
import CountryList from "country-list-with-dial-code-and-flag";

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "");

interface FormData {
  name: string;
  companyName: string;
  email: string;
  country: string;
  phone: string;
  screenCount: string;
  deploymentCountry: string;
  organizationType: string;
}

interface FormErrors {
  name?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  screenCount?: string;
  deploymentCountry?: string;
}

export default function CustomPricingForm() {
  const router = useRouter();
  const { geoData, isLoading: geoLoading } = useGeoLocation();
  const { getAllTrackingData } = useUTMContext();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    companyName: "",
    email: "",
    country: "US",
    phone: "",
    screenCount: "",
    deploymentCountry: "",
    organizationType: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [phoneValue, setPhoneValue] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<"US" | string>("US");
  const [selectedDialCode, setSelectedDialCode] = useState<string>("+1");
  const [isFormReady, setIsFormReady] = useState(false);

  // Comprehensive country data with phone codes
  const countryData = useMemo(() => {
    const countries = CountryList.getAll();

    // Group countries by code to handle secondary dial codes
    const countryMap = new Map();

    countries.forEach((country) => {
      const code = country.code;
      if (!countryMap.has(code)) {
        countryMap.set(code, {
          code: country.code,
          name: country.name,
          flag: country.flag,
          dialCodes: [],
        });
      }

      // Add dial code to the country's dial codes array
      if (country.dialCode) {
        countryMap.get(code).dialCodes.push(country.dialCode);
      }
    });

    // Convert to array and sort
    return Array.from(countryMap.values())
      .map((country) => ({
        ...country,
        phoneCode: country.dialCodes[0] || "+1", // Use first dial code as default
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  // Get available dial codes for selected country
  const getAvailableDialCodes = useCallback(
    (countryCode: string) => {
      const country = countryData.find((c) => c.code.toLowerCase() === countryCode.toLowerCase());
      return country?.dialCodes || ["+1"];
    },
    [countryData]
  );

  // Get current country data
  const currentCountry = useMemo(() => {
    return countryData.find((c) => c.code.toLowerCase() === formData.country.toLowerCase());
  }, [countryData, formData.country]);

  // Auto-detect country from IP
  useEffect(() => {
    if (geoData?.countryCode) {
      const detectedCountry = geoData.countryCode.toLowerCase();
      setSelectedCountry(detectedCountry);
      setFormData((prev) => ({ ...prev, country: detectedCountry }));

      // Set the first available dial code for the detected country
      const availableDialCodes = getAvailableDialCodes(detectedCountry);
      setSelectedDialCode(availableDialCodes[0]);
    }
    // Mark form as ready after geo detection completes (success or failure)
    if (!geoLoading) {
      setIsFormReady(true);
    }
  }, [geoData, countryData, getAvailableDialCodes, geoLoading]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const sanitizedPhone = sanitizePhoneNumber(phoneValue);

    if (!sanitizedPhone) {
      newErrors.phone = "Phone number is required";
    } else if (sanitizedPhone.length < 4 || sanitizedPhone.length > 15) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.screenCount.trim()) {
      newErrors.screenCount = "Number of screens is required";
    }

    if (!formData.deploymentCountry.trim()) {
      newErrors.deploymentCountry = "Deployment country is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If country changes, reset dial code to first available option
    if (name === "country") {
      const availableDialCodes = getAvailableDialCodes(value);
      setSelectedDialCode(availableDialCodes[0]);
    }

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Get reCAPTCHA token
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha || !grecaptcha.execute) {
        throw new Error("reCAPTCHA not loaded. Please refresh the page and try again.");
      }

      // Wait a bit for reCAPTCHA to be ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      const token = await grecaptcha.execute(env.NEXT_PUBLIC_GRECAPTCHA_KEY, {
        action: "submit",
      });

      if (!token) {
        throw new Error("Failed to get reCAPTCHA token. Please try again.");
      }

      const sanitizedPhone = sanitizePhoneNumber(phoneValue);

      // Prepare form details
      const details = {
        name: formData.name,
        email: formData.email,
        phone: `${selectedDialCode}${sanitizedPhone}`,
        company_name: formData.companyName,
        description: formData.organizationType
          ? `Custom pricing request - ${formData.screenCount} screens. Organization Type: ${formData.organizationType}. Deployment Country: ${formData.deploymentCountry}`
          : `Custom pricing request - ${formData.screenCount} screens. Deployment Country: ${formData.deploymentCountry}`,
        token: token,
        leads_source: "Custom Pricing Page",
        screen_count: formData.screenCount,
        deployment_country: formData.deploymentCountry,
        organization_type: formData.organizationType,
        form_name: "custom_pricing",
        submitted_at: new Date().toISOString(),
        address: {
          country_code: geoData?.countryCode,
          city: geoData?.city,
          region: geoData?.region,
          country:selectedCountry
        },
        ...getAllTrackingData(),
      };

      // Submit to API
      const res = await fetch(env.NEXT_PUBLIC_WIDGET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          details: details,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (result.success) {
        setSubmitStatus("success");

        // Dispatch Mixpanel tracking event
        window.dispatchEvent(
          new CustomEvent("mixpanelEvent", {
            detail: {
              type: "custom-pricing-submitted",
              data: {
                email: formData.email,
                name: formData.name,
                company_name: formData.companyName,
                phone: `${selectedDialCode}${sanitizedPhone}`,
                screen_count: formData.screenCount,
                deployment_country: formData.deploymentCountry,
                organization_type: formData.organizationType,
              },
            },
          })
        );

        // Reset form
        setFormData({
          name: "",
          companyName: "",
          email: "",
          country: selectedCountry,
          phone: "",
          screenCount: "",
          deploymentCountry: "",
          organizationType: "",
        });
        setPhoneValue("");

        // Redirect to thank you page after a short delay
        setTimeout(() => {
          router.push("/company/thank-you/");
        }, 500);
      } else {
        throw new Error(result.message || "Submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col w-full bg-white shadow-default rounded-lg">
      {!isFormReady ? (
        <div className="w-full flex items-center justify-center py-8 px-6 md:py-10 md:px-9 rounded-lg min-h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <Typography variant="small" as="p" className="text-text-secondary">
              Loading form...
            </Typography>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="w-full flex flex-col gap-4 py-8 px-6 md:py-10 md:px-9 rounded-lg"
        >
          <Typography variant="h3" as="h2" className="mb-2">
            Contact Information
          </Typography>

          <Input
            id="name"
            name="name"
            type="text"
            label="Full Name*"
            placeholder="Your full name"
            size="md"
            value={formData.name}
            onChange={handleInputChange}
            error={errors.name}
            required
          />

          <Input
            id="companyName"
            name="companyName"
            type="text"
            label="Company Name*"
            placeholder="Your company name"
            size="md"
            value={formData.companyName}
            onChange={handleInputChange}
            error={errors.companyName}
            required
          />

          <Input
            id="email"
            name="email"
            type="email"
            label="Company Email*"
            placeholder="your.email@company.com"
            size="md"
            value={formData.email}
            onChange={handleInputChange}
            error={errors.email}
            required
          />

          {/* Phone Number */}
          <div className="flex flex-col gap-1.5">
            <label id="phone-label" className="text-sm font-medium text-text-primary">
              Mobile Number*
            </label>
            <div className="flex flex-row w-full gap-2">
              {/* Country flag trigger with native select overlay showing names */}
              <div className="relative">
                <div className="w-10 h-11 rounded-lg border border-[#D7D9DE] bg-white flex items-center justify-center text-base">
                  <span aria-hidden>{currentCountry?.flag || "🇺🇸"}</span>
                </div>
                <select
                  id="country"
                  name="country"
                  aria-label="Country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="absolute inset-0 w-14 h-12 opacity-0 cursor-pointer"
                >
                  {countryData.map((country) => (
                    <option key={country.code} value={country.code.toLowerCase()}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Phone Input with Country Code */}
              <div className="h-11 w-full rounded-lg border border-[#D7D9DE] bg-white px-4 flex items-center gap-2 text-lg text-text-secondary">
                {/* Dial Code Selector - only show if multiple options */}
                {currentCountry && currentCountry.dialCodes.length > 1 ? (
                  <select
                    value={selectedDialCode}
                    onChange={(e) => setSelectedDialCode(e.target.value)}
                    className="text-text-primary bg-transparent outline-none border-none cursor-pointer text-sm md:text-base font-medium"
                  >
                    {currentCountry.dialCodes.map((dialCode: string) => (
                      <option key={dialCode} value={dialCode}>
                        {dialCode}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-text-primary text-sm md:text-base font-medium">
                    {selectedDialCode}
                  </span>
                )}
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={phoneValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPhoneValue(value);
                    setErrors((prev) => {
                      if (!prev.phone) {
                        return prev;
                      }
                      return { ...prev, phone: undefined };
                    });
                  }}
                  className="w-full h-full bg-transparent outline-none text-text-primary placeholder:text-text-secondary/80 text-sm md:text-base font-medium"
                  inputMode="tel"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  aria-labelledby="phone-label"
                />
              </div>
            </div>
            {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone}</p>}
          </div>

          <Input
            id="screenCount"
            name="screenCount"
            type="text"
            label="1. Number of Screens You Plan to Deploy*"
            placeholder="Give us a number"
            size="md"
            value={formData.screenCount}
            onChange={handleInputChange}
            error={errors.screenCount}
            required
          />

          <Input
            id="deploymentCountry"
            name="deploymentCountry"
            type="text"
            label="2. Country Where You Plan to Deploy Your Screens*"
            placeholder="USA, UK, South Asia like this"
            size="md"
            value={formData.deploymentCountry}
            onChange={handleInputChange}
            error={errors.deploymentCountry}
            required
          />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="organizationType" className="text-sm font-medium text-text-primary">
              3. We Provide Special Quotation for Charities & Educational Institutions
            </label>
            <textarea
              id="organizationType"
              name="organizationType"
              aria-labelledby="organizationType"
              placeholder="Tell us a little bit about yourself"
              value={formData.organizationType}
              onChange={handleInputChange}
              className="min-h-[96px] w-full rounded-md border border-gray-300 bg-white text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition p-4 text-sm md:text-base font-medium"
            />
          </div>

          {/* Status Messages */}
          {submitStatus === "success" && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm">
                Thank you! Your request has been submitted successfully. We&apos;ll get back to you
                soon with a custom pricing plan.
              </p>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">
                Sorry, there was an error submitting your request. Please try again or contact us
                directly at contact@pickcel.com
              </p>
            </div>
          )}

          <NewButton
            type="submit"
            variant="primary"
            className="w-full text-lg px-0 mt-2"
            disabled={isSubmitting}
            size="lg"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </NewButton>

          <p className="text-sm text-center text-text-secondary mt-2">
            The quoting process can take up to a few days. If you have questions, please write to
            us at{" "}
            <a href="mailto:contact@pickcel.com" className="text-primary underline hover:text-accent hover:underline">
              contact@pickcel.com
            </a>
          </p>
        </form>
      )}
    </div>
  );
}

